import { EnhancedBaseService } from '../base/EnhancedBaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

import type { Database, Json } from '@/integrations/supabase/types';
import { CONCEPT_TASK_TYPE, isActiveConcept } from '@/lib/briefs/concept-contract';
import { approvedCopyOffDeliveryChannel, hasApprovedCopy } from '@/lib/briefs/concept-copy-state';
import { getConnectedPlatforms } from '@/lib/connections/getConnectedPlatforms';
import {
  type StoredProjectStrategy,
  hasMaterializedProjectStrategy,
} from '@/lib/projects/project-strategy';
import { logger } from '@/utils/logger';

/** Mirrors `projects_kind_check` in the database. 'brief' was added by
 *  migration 86 — without it here, the `(row.kind as ProjectKind) || 'campaign'`
 *  mapping below would keep briefs indistinguishable from campaigns on the
 *  client, which is the exact defect that migration fixed server-side. */
export type ProjectKind = 'launch' | 'growth' | 'campaign' | 'series' | 'custom' | 'brief';

/**
 * THE KINDS THAT ARE PLANS. `projects` is single-table inheritance: it stores
 * the Plan/Campaign product AND the Brief product, discriminated only by
 * `kind`, and nothing in the schema enforces which one a query wanted.
 *
 * Measured on prod 2026-08-28, BEFORE this list existed: the plans list
 * (`listCampaignsWithCoverage`, ordered `updated_at DESC LIMIT 20`) returned
 * **12 campaigns and 8 BRIEFS**. Briefs are the newest rows in the table, so
 * they had colonised 40% of a list that is not about them. The same root cause
 * printed "36 briefs" on a page whose own tab said 4, and puts 5 rows in
 * `marketing_delivery_facts_v1` claiming a `brief_id` when 0 of them are
 * Briefs.
 *
 * 🔴 AN ALLOWLIST, NOT `.neq('kind','brief')`. Excluding the one kind you have
 * thought of is how the NEXT noun added to this table silently joins the plans
 * list. A new kind must be added here deliberately, and
 * `__tests__/CampaignService.kind-scope.test.ts` fails until it is.
 *
 * `series` is included because it is in the type and the `KindFilter` UI, but
 * no writer in the codebase ever produces it — it is inert, and listing it
 * costs nothing while removing it from the type is a separate cleanup.
 */
export const PLAN_KINDS = ['campaign', 'growth', 'launch', 'custom', 'series'] as const;

export type CampaignStatus =
  'draft' | 'active' | 'generating' | 'partial' | 'completed' | 'scheduled' | 'archived';

/**
 * Brief delivery targets are derived from Concept intent rows. `projects.platforms`
 * remains a legacy project field and is not a source of truth for Brief cards.
 */
export interface BriefDeliveryIntentSummary {
  intent: 'organic' | 'paid';
  platform: string | null;
  conceptCount: number;
}

export interface CampaignAiSummary {
  headline: string;
  insights: Array<{ highlight: string; text: string }>;
  generatedAt: string;
  coverageAtGeneration: number;
  version: number;
  accepted?: boolean;
  dismissedAt?: string;
}

export interface WorkflowStep {
  step_id: number;
  action: string;
  tools?: string[];
  /** Optional present-progressive label, per Claude Code's activeForm convention. */
  active_form?: string;
  /** Optional copy shown when this step is the active step. */
  description?: string;
  /** When true, the agent must call request_user_approval after the step's tool returns. */
  requires_approval?: boolean;
  /** P1 — what this step consumes (e.g. "30s script"). Surfaces in the agent's ACTIVE WORKFLOW block. */
  uses?: string;
  /** P1 — what this step produces (e.g. "still image"). */
  creates?: string;
  /** P1 — prior step whose `creates` feeds this step's `uses`. */
  inputs_from_step_id?: number;
}

export type WorkflowOutputKind =
  'video' | 'image' | 'post' | 'carousel' | 'strategy' | 'document' | 'brand-guidelines';

export interface WorkflowTemplate {
  id: string;
  name: string;
  description: string | null;
  steps: WorkflowStep[];
  created_at: string;
  /** P1 — kinds of artifact this template ships (used by the library preview's "What it creates" row). */
  output_kinds?: WorkflowOutputKind[];
  /** P1 — low end of the time-to-finish range, in minutes. */
  estimate_minutes_min?: number | null;
  /** P1 — high end of the time-to-finish range, in minutes. */
  estimate_minutes_max?: number | null;
  /** P1 — template revision counter for §21.6 versioning. */
  version?: number;
  /** P3.2 — owner. NULL = built-in (everyone). Non-null = the user who authored it. */
  user_id?: string | null;
  /** 1.7 — jsonb bag stored in DB; consumers read icon_id / input_kind / tier here. */
  metadata?: {
    input_kind?: 'article' | 'photos' | 'url' | 'manual';
    icon_id?: string;
    tier?: 'S' | 'A' | 'B';
    required_brief_keys?: string[];
  };
}

export interface WorkflowHistoryEntry {
  step_id: number;
  status: string;
  artifacts?: Array<Record<string, unknown>>;
}

export interface WorkflowState {
  current_step_id: number;
  status: 'not_started' | 'running' | 'awaiting_approval' | 'completed' | 'failed';
  history: WorkflowHistoryEntry[];
  /** When status === 'awaiting_approval', the artifact pending review (asset URL, post id, etc.). */
  pending_artifact?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface Campaign {
  id: string;
  userId: string;
  name: string;
  goal: string | null;
  timelineStart: string | null;
  timelineEnd: string | null;
  chatSessionId: string | null;
  status: CampaignStatus;
  strategy: StoredProjectStrategy | null;
  platforms: string[] | null;
  planType: string | null;
  collectionId: string | null;
  projectId: string | null;
  kind: ProjectKind;
  notes: string | null;
  icon: string | null;
  color: string | null;
  aiSummary: CampaignAiSummary | null;
  /**
   * BV-1 W1: per-project default brand voice. Auto-applies to new chat
   * sessions opened against this project when `chat_sessions.active_brand_voice_id`
   * is NULL (priority chain: session pin > project pin > localStorage default).
   * Ownership trigger validates `user_brand_voices.user_id === projects.user_id`.
   */
  brandVoiceId: string | null;
  /** projects.brand_kit_id — project-default brand kit pin (Project rail),
   *  mirroring brandVoiceId's resolution symmetry (BI-milestone fix #5). */
  brandKitId: string | null;
  /** Optimistic-locking counter — read by `assign_post_to_slot` RPC for CAS. */
  version: number;
  createdAt: string;
  updatedAt: string;
  activeWorkflowTemplateId: string | null;
  workflowState: WorkflowState | null;
  /** P3 — typed brief threaded into the workflow agent context (RC-4 schema). */
  workflowBrief: Record<string, unknown> | null;
  /** Archive is an independent visibility axis; it never changes lifecycle status. */
  archivedAt: string | null;
  /** Present on the Brief collection reader only. */
  briefDeliveryIntents?: BriefDeliveryIntentSummary[];
  /** Present on the Brief collection reader only — ACTIVE (non-archived)
   *  `creative-concept` project_tasks under this Brief, across every delivery
   *  intent. Archived Concepts leave this denominator exactly as they leave
   *  the one on the Brief page. */
  conceptCount?: number;
  /**
   * Present on the Brief collection reader only — how many of this Brief's
   * active Concepts hold at least one APPROVED `ad_units` row, decided by the
   * shared `hasApprovedCopy` predicate. A written draft does not count. The
   * denominator is `conceptCount`.
   *
   * ⚠️ It used to count the Concepts carrying a `project_tasks` SELECTION
   * pointer, which answers a different question — the one the paid handoff
   * asks. The Brief page answered "does copy exist" while this answered "is
   * copy pinned", the two printed different fractions for one Brief on
   * 2026-08-27, and the detail page's disagreeing `2/2` went terminal green.
   * One predicate now serves both.
   *
   * This is the ONLY progress fraction a Brief row can honestly carry today.
   * A delivery fraction ("how many of these went out") is deliberately absent.
   * 🔴 **CORRECTED 2026-08-31** — this used to blame `private.brief_copy_revision_capability.state
   * = 'groundwork'`; that flag has been `database_ready` since the 2026-08-26 activation migration.
   * The real reason no Brief-scoped delivery count is readable today is that
   * `paid_media_input_bindings` — the join key a count would read through — has zero rows in
   * production (built, never exercised; see `src/components/briefs/README.md` § Brief → paid
   * campaign handoff). The Briefs list states that limitation in one line rather than drawing a bar
   * over a number it cannot read.
   */
  conceptsWithCopyCount?: number;
  /** Brief collection reader only: how many of the Concepts counted in
   *  `conceptsWithCopyCount` have approved copy that is not for the paid
   *  channel the Concept names. The card greys its approved-copy bar for these. */
  conceptsWithCopyOffChannelCount?: number;
  /** Present on the Brief collection reader only. First attached Persona by
   *  `project_audience_refs.position`, plus how many are attached in total —
   *  a Brief can carry more than one. Null when no Persona is attached yet. */
  personaSummary?: { name: string; totalCount: number } | null;
  /** Brief collection reader only: the status of the Brief's NEWEST `brief_runs` row, or null
   *  when it has none (a Manual Brief). A `failed` run is why a Brief still reads as Draft. */
  latestRunStatus?: string | null;
}

export interface CampaignWithCoverage extends Campaign {
  coverageCreated: number;
  coverageTotal: number;
  coveragePct: number;
  /** Name of `activeWorkflowTemplateId`'s template, when one is assigned — for
   *  the list/grid card's assigned-workflow badge tooltip. Null when no
   *  workflow is assigned, undefined if the surface didn't request it. */
  activeWorkflowTemplateName?: string | null;
}

export interface CreateCampaignInput {
  userId: string;
  name: string;
  goal?: string;
  notes?: string;
  timelineStart?: string;
  timelineEnd?: string;
  chatSessionId?: string;
  strategy?: StoredProjectStrategy | null;
  platforms?: string[];
  planType?: string;
  collectionId?: string;
  projectId?: string;
  kind?: ProjectKind;
  icon?: string | null;
  color?: string | null;
  brandVoiceId?: string | null;
  brandKitId?: string | null;
  activeWorkflowTemplateId?: string | null;
  workflowState?: WorkflowState | null;
  workflowBrief?: Record<string, unknown> | null;
}

export interface UpdateCampaignInput {
  name?: string;
  goal?: string;
  status?: CampaignStatus;
  timelineStart?: string;
  timelineEnd?: string;
  chatSessionId?: string;
  strategy?: StoredProjectStrategy | null;
  planType?: string;
  platforms?: string[];
  collectionId?: string | null;
  projectId?: string | null;
  notes?: string | null;
  icon?: string | null;
  color?: string | null;
  brandVoiceId?: string | null;
  brandKitId?: string | null;
  activeWorkflowTemplateId?: string | null;
  workflowState?: WorkflowState | null;
  workflowBrief?: Record<string, unknown> | null;
  /**
   * Archive is an orthogonal visibility axis. It must never be inferred from
   * or used to overwrite the project's real lifecycle status.
   */
  archivedAt?: string | null;
}

type JsonObject = { [key: string]: Json | undefined };
type ProjectUpdate = Database['public']['Tables']['projects']['Update'];
type WorkflowTemplateUpdate = Database['public']['Tables']['workflow_templates']['Update'];

/**
 * JSONB columns are a persistence boundary: callers may work with richer
 * TypeScript records, but only serializable values can cross into PostgREST.
 * Normalizing here matches the payload PostgREST receives while making an
 * invalid value fail at the boundary rather than widening generated DB types.
 */
function toJson(value: unknown): Json {
  const serialized = JSON.stringify(value);
  if (serialized === undefined) {
    throw new TypeError('Expected a JSON-serializable value');
  }
  return JSON.parse(serialized) as Json;
}

function toJsonObject(value: unknown): JsonObject {
  const normalized = toJson(value);
  if (!normalized || typeof normalized !== 'object' || Array.isArray(normalized)) {
    throw new TypeError('Expected a JSON object');
  }
  return normalized;
}

function readWorkflowState(value: Json | null | undefined): WorkflowState | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  if (typeof value.current_step_id !== 'number' || !Array.isArray(value.history)) return null;
  if (
    value.status !== 'not_started' &&
    value.status !== 'running' &&
    value.status !== 'awaiting_approval' &&
    value.status !== 'completed' &&
    value.status !== 'failed'
  ) {
    return null;
  }
  return value as unknown as WorkflowState;
}

const MAX_COLLECTION_NAME_ATTEMPTS = 5;

export class CampaignServiceClass extends EnhancedBaseService {
  /**
   * The workspace (brand kit) this user is currently acting in, or null.
   *
   * Always via `get_effective_brand_kit()` — the zero-arg, `authenticated`
   * entry point that resolves through `auth.uid()`. Never re-derive the rule
   * here: it lives once, in `resolve_effective_brand_kit_id()` in Postgres
   * (this user's pointer -> the account default -> most recent). Five separate
   * hand-rolled copies of that rule is exactly the defect the workspace work
   * removed, and a sixth would be indistinguishable from the first five.
   *
   * The actor-taking variant (`get_effective_brand_kit_for`) is service_role
   * only and must never be reachable from a browser client.
   */
  private async resolveActiveBrandKitId(): Promise<string | null> {
    try {
      const { data, error } = await this.client.rpc('get_effective_brand_kit');
      if (error || !Array.isArray(data) || data.length === 0) return null;
      return (data[0] as { id?: string }).id ?? null;
    } catch {
      return null;
    }
  }

  async createCampaign(input: CreateCampaignInput): Promise<ServiceResponse<Campaign>>;
  async createCampaign(
    userIdOrInput: string | CreateCampaignInput,
    name?: string,
    goal?: string,
    timelineStart?: string,
    timelineEnd?: string,
    chatSessionId?: string,
    strategy?: StoredProjectStrategy | null,
    platforms?: string[],
    planType?: string,
    collectionId?: string,
    projectId?: string,
  ): Promise<ServiceResponse<Campaign>> {
    const input: CreateCampaignInput =
      typeof userIdOrInput === 'string'
        ? {
            userId: userIdOrInput,
            name: name!,
            goal,
            timelineStart,
            timelineEnd,
            chatSessionId,
            strategy,
            platforms,
            planType,
            collectionId,
            projectId,
          }
        : userIdOrInput;

    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();

      // Resolved BEFORE the collection block, not after. It used to be
      // computed just above the `projects` insert, which meant the
      // media_collections row provisioned a few lines earlier was created
      // unattributed — a project landed in the right workspace while its own
      // asset collection belonged to none.
      //
      // An explicit `input.brandKitId` still wins (including an explicit null,
      // which stays a deliberate "belongs to no particular workspace"). Only
      // `undefined` — "the caller didn't say" — resolves to the active one.
      //
      // Failure resolves to null rather than throwing: a project that exists
      // unattributed is recoverable; a project that could not be created
      // because a brand lookup hiccuped is not.
      const resolvedBrandKitId =
        input.brandKitId === undefined ? await this.resolveActiveBrandKitId() : input.brandKitId;

      let activeCollectionId = input.collectionId;
      let newlyCreatedCollectionId: string | null = null;

      if (!activeCollectionId) {
        const goalColors: Record<string, string> = {
          brand_awareness: 'indigo',
          conversion: 'emerald',
          engagement: 'amber',
          seasonal: 'rose',
        };
        // SELECT-then-INSERT pattern. The previous upsert used
        // `onConflict: 'user_id,parent_id,name'` but no matching unique
        // constraint exists on `media_collections`, so Postgres would
        // return 42P10 ("no unique or exclusion constraint matching the
        // ON CONFLICT specification") and the create flow died silently.
        // Fix: look up an existing collection by (user, parent, name);
        // create one if not found.
        const lookupCollection = async (name: string) => {
          const scoped = this.client
            .from('media_collections')
            .select('id')
            .eq('user_id', ownerId)
            .is('parent_id', null)
            .eq('name', name);
          const { data } = await (
            resolvedBrandKitId
              ? scoped.eq('brand_kit_id', resolvedBrandKitId)
              : scoped.is('brand_kit_id', null)
          ).maybeSingle();
          return (data?.id as string | undefined) ?? null;
        };
        const insertCollection = (name: string) =>
          this.client
            .from('media_collections')
            .insert({
              user_id: ownerId,
              name,
              icon: 'megaphone',
              color: goalColors[input.goal || 'brand_awareness'] || 'slate',
              parent_id: null,
              brand_kit_id: resolvedBrandKitId ?? null,
            })
            .select('id')
            .single();

        const existingId = await lookupCollection(input.name);
        if (existingId) {
          activeCollectionId = existingId;
        } else {
          let attempt = 0;
          let collectionName = input.name;
          while (!activeCollectionId) {
            const { data: created, error: collErr } = await insertCollection(collectionName);
            if (!collErr) {
              if (!created) throw new Error('Failed to provision media_collection for campaign');
              activeCollectionId = created.id;
              newlyCreatedCollectionId = created.id;
              break;
            }
            if (collErr.code !== '23505') {
              throw new Error(`Collection provision failed: ${collErr.message}`);
            }
            const concurrentId = await lookupCollection(collectionName);
            if (concurrentId) {
              activeCollectionId = concurrentId;
              break;
            }
            attempt += 1;
            if (attempt > MAX_COLLECTION_NAME_ATTEMPTS) {
              throw new Error('Collection provision conflicted but could not be reloaded');
            }
            collectionName = `${input.name} (${attempt + 1})`;
          }
        }
      }

      // Stamp the workspace this project is being created IN.
      //
      // `brand_kit_id` has been plumbed here for a while, but every caller left
      // it undefined — which is why 129 of 130 production projects are NULL and
      // why switching workspace changes almost nothing on the content surfaces.
      // Defaulting at this ONE seam fixes every caller at once, instead of
      // hunting them down and having the next new one forget again.
      const { data, error } = await this.client
        .from('projects')
        .insert({
          user_id: ownerId,
          name: input.name,
          goal: input.goal || null,
          notes: input.notes ?? null,
          icon: input.icon ?? null,
          color: input.color ?? null,
          status: 'draft',
          timeline_start: input.timelineStart || null,
          timeline_end: input.timelineEnd || null,
          chat_session_id: input.chatSessionId || null,
          strategy: input.strategy || {},
          platforms: input.platforms || null,
          plan_type: input.planType || null,
          collection_id: activeCollectionId || null,
          kind: input.kind || 'campaign',
          brand_voice_id: input.brandVoiceId ?? null,
          brand_kit_id: resolvedBrandKitId ?? null,
          active_workflow_template_id: input.activeWorkflowTemplateId ?? null,
          workflow_state: input.workflowState === undefined ? null : toJson(input.workflowState),
          workflow_brief: input.workflowBrief === undefined ? null : toJson(input.workflowBrief),
        })
        .select()
        .single();

      if (error) {
        if (newlyCreatedCollectionId) {
          const { data: deletedCollections, error: cleanupError } = await this.client
            .from('media_collections')
            .delete()
            .eq('id', newlyCreatedCollectionId)
            .select('id');
          if (cleanupError || !deletedCollections || deletedCollections.length === 0) {
            const cleanupMessage = cleanupError?.message ?? 'no collection row was deleted';
            throw new Error(
              `Campaign create failed: ${error.message}. Collection cleanup failed: ${cleanupMessage}`,
            );
          }
        }
        throw new Error(`Campaign create failed: ${error.message}`);
      }
      return this.mapRow(data);
    });
  }

  async getCampaign(id: string): Promise<ServiceResponse<Campaign | null>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('projects')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw new Error(`Campaign fetch failed: ${error.message}`);
      return data ? this.mapRow(data) : null;
    });
  }

  /**
   * Fetch one typed Brief without allowing a campaign/project row to leak into
   * the Brief workspace. The route boundary matters: Briefs share storage with
   * projects, but they do not share the same detail contract.
   */
  async getBrief(id: string): Promise<ServiceResponse<Campaign | null>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('projects')
        .select('*')
        .eq('id', id)
        .eq('kind', 'brief')
        .maybeSingle();

      if (error) throw new Error(`Brief fetch failed: ${error.message}`);
      return data ? this.mapRow(data) : null;
    });
  }

  async listCampaigns(
    userId: string,
    collectionId?: string,
    projectId?: string,
    limit = 20,
  ): Promise<ServiceResponse<Campaign[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      void userId;
      const criteria: Record<string, any> = {};
      if (collectionId) criteria.collection_id = collectionId;
      void projectId;

      // P4.3 — exclude archived projects from the default list view. The
      // archived view will be a separate UI surface (deferred). Today's
      // /projects route should only show live projects.
      let query = this.client
        .from('projects')
        .select('*')
        .is('archived_at', null)
        .order('updated_at', { ascending: false })
        .limit(limit);
      if (Object.keys(criteria).length > 0) query = query.match(criteria);

      // Scope to the active workspace, keeping unattributed projects visible.
      //
      // `brand_kit_id IS NULL` is NOT a gap to filter away: 129 of 130
      // production projects predate workspace stamping and were deliberately
      // never backfilled, because the account's current default is no evidence
      // of what an old project was actually for. Filtering them out would empty
      // this list the first time anyone switched. New projects are stamped at
      // creation (see `createCampaign`), so this narrows naturally over time
      // instead of rewriting history.
      const activeKitId = await this.resolveActiveBrandKitId();
      if (activeKitId) {
        query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
      }

      const { data, error } = await query;

      if (error) throw new Error(`Campaign list failed: ${error.message}`);
      return (data || []).map((row: Record<string, unknown>) => this.mapRow(row));
    });
  }

  /**
   * Briefs share the `projects` storage table but are a distinct typed object.
   * Keep this reader separate from `listCampaigns`: the generic project list
   * deliberately hides archived rows, while the Briefs workspace owns an
   * Archived lifecycle tab and must never pull campaign/project kinds into it.
   */
  async listBriefs(limit = 100, includeArchived = true): Promise<ServiceResponse<Campaign[]>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();

      // Briefs belong to a workspace like every other project. Without this
      // the Briefs list showed every workspace's briefs at once, and switching
      // workspace changed nothing. Legacy rows with no brand_kit_id stay
      // visible everywhere -- most projects predate the column, and hiding
      // them would empty the list for anyone who has ever switched. The
      // Briefs page applies the same rule through `isBriefInWorkspace`
      // (src/lib/briefs/workspace-briefs.ts) and marks those cards.
      const activeKitId = await this.resolveActiveBrandKitId();

      let query = this.client
        .from('projects')
        .select('*')
        .eq('kind', 'brief')
        .eq('user_id', ownerId);
      if (activeKitId) {
        query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
      }
      if (!includeArchived) query = query.is('archived_at', null);

      const { data, error } = await query.order('updated_at', { ascending: false }).limit(limit);

      if (error) throw new Error(`Brief list failed: ${error.message}`);
      const briefs = (data || []).map((row: Record<string, unknown>) => this.mapRow(row));
      if (briefs.length === 0) return [];

      const briefIds = briefs.map((brief) => brief.id);

      const { data: runRows, error: runsError } = await this.client
        .from('brief_runs')
        .select('project_id, status, created_at')
        .in('project_id', briefIds)
        .order('created_at', { ascending: false });
      if (runsError) throw new Error(`Brief run list failed: ${runsError.message}`);

      const latestRunStatusByBrief = new Map<string, string>();
      for (const run of runRows ?? []) {
        if (!latestRunStatusByBrief.has(run.project_id)) {
          latestRunStatusByBrief.set(run.project_id, run.status);
        }
      }

      const { data: audienceRefRows, error: audienceRefsError } = await this.client
        .from('project_audience_refs')
        .select('project_id, position, brand_audiences ( name )')
        .eq('user_id', ownerId)
        .in('project_id', briefIds)
        .order('position', { ascending: true });
      if (audienceRefsError)
        throw new Error(`Brief Persona list failed: ${audienceRefsError.message}`);

      const personaSummaryByBrief = new Map<string, { name: string; totalCount: number }>();
      for (const ref of audienceRefRows ?? []) {
        const relation = ref.brand_audiences as { name: string } | { name: string }[] | null;
        const persona = Array.isArray(relation) ? relation[0] : relation;
        if (!persona?.name) continue;
        const existing = personaSummaryByBrief.get(ref.project_id);
        if (existing) {
          existing.totalCount += 1;
        } else {
          personaSummaryByBrief.set(ref.project_id, { name: persona.name, totalCount: 1 });
        }
      }

      const { data: conceptRows, error: conceptsError } = await this.client
        .from('project_tasks')
        .select('id, project_id, status')
        .eq('user_id', ownerId)
        .eq('type', CONCEPT_TASK_TYPE)
        .in('project_id', briefIds);
      if (conceptsError) throw new Error(`Brief Concept list failed: ${conceptsError.message}`);

      // Archived Concepts leave BOTH the numerator and the denominator, which
      // is what the Brief page already does. Counting them here and not there
      // is the same divergence in a second place.
      const activeConceptRows = (conceptRows ?? []).filter((concept) =>
        isActiveConcept(concept.status),
      );

      const conceptToBrief = new Map<string, string>();
      const conceptCountByBrief = new Map<string, number>();
      for (const concept of activeConceptRows) {
        conceptToBrief.set(concept.id, concept.project_id);
        conceptCountByBrief.set(
          concept.project_id,
          (conceptCountByBrief.get(concept.project_id) ?? 0) + 1,
        );
      }

      // The card counts APPROVED copy, through the same predicate the Brief page,
      // the door and the chain read (`hasApprovedCopy`). A written draft is not
      // approved, so a Concept holding only drafts adds nothing to the numerator.
      const adUnitsByConcept = new Map<
        string,
        Array<{ status: string; adPlatform: string; copy: unknown }>
      >();
      if (conceptToBrief.size > 0) {
        const { data: adUnitRows, error: adUnitsError } = await this.client
          .from('ad_units')
          .select('project_task_id, status, ad_platform, copy')
          .eq('user_id', ownerId)
          .in('project_task_id', [...conceptToBrief.keys()]);
        if (adUnitsError) throw new Error(`Brief ad copy list failed: ${adUnitsError.message}`);
        for (const unit of adUnitRows ?? []) {
          if (!unit.project_task_id) continue;
          const units = adUnitsByConcept.get(unit.project_task_id) ?? [];
          units.push({ status: unit.status, adPlatform: unit.ad_platform, copy: unit.copy });
          adUnitsByConcept.set(unit.project_task_id, units);
        }
      }

      const copyCountByBrief = new Map<string, number>();
      for (const [conceptId, briefId] of conceptToBrief) {
        if (!hasApprovedCopy({ adUnits: adUnitsByConcept.get(conceptId) ?? [] })) continue;
        copyCountByBrief.set(briefId, (copyCountByBrief.get(briefId) ?? 0) + 1);
      }
      if (conceptToBrief.size === 0) {
        return briefs.map((brief) => ({
          ...brief,
          briefDeliveryIntents: [],
          conceptCount: 0,
          conceptsWithCopyCount: 0,
          personaSummary: personaSummaryByBrief.get(brief.id) ?? null,
          latestRunStatus: latestRunStatusByBrief.get(brief.id) ?? null,
        }));
      }

      const { data: intentRows, error: intentsError } = await this.client
        .from('project_task_delivery_intents')
        .select('project_task_id, intent, platform')
        .eq('user_id', ownerId)
        .in('project_task_id', [...conceptToBrief.keys()]);
      if (intentsError)
        throw new Error(`Brief delivery intent list failed: ${intentsError.message}`);

      const summariesByBrief = new Map<string, Map<string, BriefDeliveryIntentSummary>>();
      for (const intent of intentRows ?? []) {
        if (intent.intent !== 'organic' && intent.intent !== 'paid') continue;
        const briefId = conceptToBrief.get(intent.project_task_id);
        if (!briefId) continue;
        const platform = intent.platform?.trim() || null;
        const key = `${intent.intent}:${platform ?? ''}`;
        const summaries =
          summariesByBrief.get(briefId) ?? new Map<string, BriefDeliveryIntentSummary>();
        const current = summaries.get(key);
        summaries.set(key, {
          intent: intent.intent,
          platform,
          conceptCount: (current?.conceptCount ?? 0) + 1,
        });
        summariesByBrief.set(briefId, summaries);
      }

      const intentsByConcept = new Map<
        string,
        Array<{ intent: string; platform: string | null }>
      >();
      for (const intent of intentRows ?? []) {
        const list = intentsByConcept.get(intent.project_task_id) ?? [];
        list.push({ intent: intent.intent, platform: intent.platform?.trim() || null });
        intentsByConcept.set(intent.project_task_id, list);
      }
      const offChannelByBrief = new Map<string, number>();
      for (const [conceptId, briefId] of conceptToBrief) {
        const adUnits = adUnitsByConcept.get(conceptId) ?? [];
        if (!hasApprovedCopy({ adUnits })) continue;
        if (!approvedCopyOffDeliveryChannel({ adUnits }, intentsByConcept.get(conceptId) ?? [])) {
          continue;
        }
        offChannelByBrief.set(briefId, (offChannelByBrief.get(briefId) ?? 0) + 1);
      }

      return briefs.map((brief) => ({
        ...brief,
        briefDeliveryIntents: [...(summariesByBrief.get(brief.id)?.values() ?? [])].sort(
          (left, right) =>
            left.intent.localeCompare(right.intent) ||
            (left.platform ?? '').localeCompare(right.platform ?? ''),
        ),
        conceptCount: conceptCountByBrief.get(brief.id) ?? 0,
        conceptsWithCopyCount: copyCountByBrief.get(brief.id) ?? 0,
        conceptsWithCopyOffChannelCount: offChannelByBrief.get(brief.id) ?? 0,
        personaSummary: personaSummaryByBrief.get(brief.id) ?? null,
        latestRunStatus: latestRunStatusByBrief.get(brief.id) ?? null,
      }));
    });
  }

  async updateCampaign(
    id: string,
    updates: UpdateCampaignInput,
  ): Promise<ServiceResponse<Campaign>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const payload: ProjectUpdate = {
        updated_at: new Date().toISOString(),
      };

      if (updates.name !== undefined) payload.name = updates.name;
      if (updates.goal !== undefined) payload.goal = updates.goal;
      if (updates.status !== undefined) payload.status = updates.status;
      if (updates.timelineStart !== undefined) payload.timeline_start = updates.timelineStart;
      if (updates.timelineEnd !== undefined) payload.timeline_end = updates.timelineEnd;
      if (updates.chatSessionId !== undefined) payload.chat_session_id = updates.chatSessionId;
      if (updates.strategy !== undefined) payload.strategy = updates.strategy ?? {};
      if (updates.platforms !== undefined) payload.platforms = updates.platforms;
      if (updates.planType !== undefined) payload.plan_type = updates.planType;
      if (updates.collectionId !== undefined) payload.collection_id = updates.collectionId;
      if (updates.notes !== undefined) payload.notes = updates.notes;
      if (updates.icon !== undefined) payload.icon = updates.icon;
      if (updates.color !== undefined) payload.color = updates.color;
      if (updates.brandVoiceId !== undefined) payload.brand_voice_id = updates.brandVoiceId;
      if (updates.brandKitId !== undefined) payload.brand_kit_id = updates.brandKitId;
      if (updates.activeWorkflowTemplateId !== undefined)
        payload.active_workflow_template_id = updates.activeWorkflowTemplateId;
      if (updates.workflowState !== undefined)
        payload.workflow_state = toJson(updates.workflowState);
      if (updates.workflowBrief !== undefined)
        payload.workflow_brief = toJson(updates.workflowBrief);
      if (updates.archivedAt !== undefined) payload.archived_at = updates.archivedAt;

      // Pass 4 P1 fix: a project that gains a meaningful strategy should
      // auto-promote `draft → active` per CLAUDE.md PJ-1 lifecycle. Previously
      // strategy saves left status='draft' even after the user shipped a real
      // plan, so the badge stayed gray and the project never appeared in the
      // active filter. Only promote when (a) caller didn't explicitly set
      // status, (b) strategy is non-empty, (c) current row is still draft.
      if (payload.status === undefined && hasMaterializedProjectStrategy(updates.strategy)) {
        const { data: current } = await this.client
          .from('projects')
          .select('status')
          .eq('id', id)
          .maybeSingle();
        if (current?.status === 'draft') {
          payload.status = 'active';
        }
      }

      const { data, error } = await this.client
        .from('projects')
        .update(payload)
        .eq('id', id)
        .select()
        .single();

      if (error) throw new Error(`Campaign update failed: ${error.message}`);
      return this.mapRow(data);
    });
  }

  async getCampaignWithCoverage(id: string): Promise<ServiceResponse<CampaignWithCoverage | null>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data: campaignData, error: campaignError } = await this.client
        .from('projects')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      let campaign = campaignData;

      if (campaignError) throw new Error(`Campaign fetch failed: ${campaignError.message}`);

      // `/projects/:id` links may pass a `collection_id` (legacy URLs without
      // `?planId=`) rather than the `projects.id` primary key — fall back.
      // Multiple projects can share a `collection_id`, so take the most
      // recently updated one rather than `.maybeSingle()`, which throws on
      // more than one match.
      if (!campaign) {
        const fallback = await this.client
          .from('projects')
          .select('*')
          .eq('collection_id', id)
          .order('updated_at', { ascending: false })
          .limit(1);
        if (fallback.error) throw new Error(`Campaign fetch failed: ${fallback.error.message}`);
        campaign = fallback.data?.[0] ?? null;
      }

      if (!campaign) return null;

      const { count, error: countError } = await this.client
        .from('social_posts')
        .select('*', { count: 'exact', head: true })
        .eq('campaign_id', campaign.id);

      if (countError) throw new Error(`Campaign post-count failed: ${countError.message}`);

      const mapped = this.mapRow(campaign);
      const coverageTotal = mapped.strategy?.schedule?.length ?? 0;
      const coverageCreated = Math.min(count || 0, coverageTotal);
      const coveragePct =
        coverageTotal > 0 ? Math.round((coverageCreated / coverageTotal) * 100) : 0;

      return { ...mapped, coverageCreated, coverageTotal, coveragePct };
    });
  }

  async listCampaignsWithCoverage(
    userId: string,
    collectionId?: string,
    limit = 20,
    includeArchived = false,
  ): Promise<ServiceResponse<CampaignWithCoverage[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();

      // Most consumers should not see archived projects. The legacy caller's
      // opt-in remains for compatibility; Archive uses listArchivedProjects so
      // it never fetches 20 mixed rows and filters them in the browser.
      let query = this.client
        .from('projects')
        .select('*')
        // The plans list is not about Briefs. Without this it returned 12
        // campaigns and 8 Briefs on prod — see `PLAN_KINDS`.
        .in('kind', PLAN_KINDS as unknown as string[])
        .order('updated_at', { ascending: false })
        .limit(limit);

      if (!includeArchived) query = query.is('archived_at', null);

      if (collectionId) query = query.eq('collection_id', collectionId);

      // Scope to the active workspace. See `listCampaigns` for why NULL rows
      // stay visible.
      //
      // ⚠️ THIS is the method the app actually calls — `usePlans.ts:40`,
      // `PlansListView`, `PlanDetailView`, `AddToProjectSubmenu`. Scoping only
      // `listCampaigns` (which has ZERO production callers) was a silent no-op:
      // the filter was written, reviewed, and would never have executed. That
      // is this repo's single most recurring bug class, and it was reintroduced
      // here by someone who had just finished writing about it. Before adding a
      // filter to a service method, grep for its callers.
      const activeKitId = await this.resolveActiveBrandKitId();
      if (activeKitId) {
        query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
      }

      const { data, error } = await query;
      if (error) throw new Error(`Campaign list-with-coverage failed: ${error.message}`);

      const campaigns = (data || []).map((row: Record<string, unknown>) => this.mapRow(row));

      const campaignIds = campaigns.map((c) => c.id);
      if (campaignIds.length === 0) return [];

      const { data: postCounts, error: countError } = await this.client
        .from('social_posts')
        .select('campaign_id')
        .in('campaign_id', campaignIds);

      if (countError) throw new Error(`Campaign post-count batch failed: ${countError.message}`);

      const countMap = new Map<string, number>();
      (postCounts || []).forEach((p: { campaign_id: string }) => {
        countMap.set(p.campaign_id, (countMap.get(p.campaign_id) || 0) + 1);
      });

      // Batch-resolve the assigned workflow template's name for the list/grid
      // card's assigned-workflow badge tooltip — one round-trip for every
      // project on the page, not N+1 per card.
      const templateIds = Array.from(
        new Set(
          campaigns.map((c) => c.activeWorkflowTemplateId).filter((id): id is string => !!id),
        ),
      );
      const templateNameMap = new Map<string, string>();
      if (templateIds.length > 0) {
        const { data: templateRows, error: templateError } = await this.client
          .from('workflow_templates')
          .select('id, name')
          .in('id', templateIds);
        if (templateError) {
          logger.error('[CampaignService] workflow template name batch failed', templateError);
        } else {
          (templateRows || []).forEach((t: { id: string; name: string }) => {
            templateNameMap.set(t.id, t.name);
          });
        }
      }

      return campaigns.map((c) => {
        const coverageTotal = c.strategy?.schedule?.length ?? 0;
        const coverageCreated = Math.min(countMap.get(c.id) || 0, coverageTotal);
        const coveragePct =
          coverageTotal > 0 ? Math.round((coverageCreated / coverageTotal) * 100) : 0;
        const activeWorkflowTemplateName = c.activeWorkflowTemplateId
          ? (templateNameMap.get(c.activeWorkflowTemplateId) ?? null)
          : null;
        return { ...c, coverageCreated, coverageTotal, coveragePct, activeWorkflowTemplateName };
      });
    });
  }

  async getActivePlan(userId: string): Promise<ServiceResponse<CampaignWithCoverage | null>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      void userId;
      // P4.3 — exclude archived projects from the global "active plan"
      // resolution. Without this filter, soft-archived stale projects
      // could re-leak into the composer ActiveStepChip (the same surface
      // P1.1 closed for the no-planId path).
      const { data, error } = await this.client
        .from('projects')
        .select('*')
        // 🔴 `limit(1)` over ALL kinds made the composer's ActiveStepChip show a
        // BRIEF as the active plan. Briefs are the newest rows in `projects`, so
        // the single most-recently-updated project was one on 2026-08-28, and so
        // were the next two. See `PLAN_KINDS`.
        .in('kind', PLAN_KINDS as unknown as string[])
        .is('archived_at', null)
        .in('status', ['draft', 'active', 'generating', 'partial', 'scheduled'])
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw new Error(`Active plan fetch failed: ${error.message}`);
      if (!data) return null;

      const mapped = this.mapRow(data);
      const { count } = await this.client
        .from('social_posts')
        .select('*', { count: 'exact', head: true })
        .eq('campaign_id', mapped.id);

      const coverageTotal = mapped.strategy?.schedule?.length ?? 0;
      const coverageCreated = Math.min(count || 0, coverageTotal);
      const coveragePct =
        coverageTotal > 0 ? Math.round((coverageCreated / coverageTotal) * 100) : 0;

      return { ...mapped, coverageCreated, coverageTotal, coveragePct };
    });
  }

  /** Project lifecycle is distinct from post/task and workflow state. */
  async completeProject(id: string): Promise<ServiceResponse<Campaign>> {
    return this.updateCampaign(id, { status: 'completed' });
  }

  async reopenProject(id: string): Promise<ServiceResponse<Campaign>> {
    return this.updateCampaign(id, { status: 'active', archivedAt: null });
  }

  async archiveProject(id: string): Promise<ServiceResponse<Campaign>> {
    return this.updateCampaign(id, { archivedAt: new Date().toISOString() });
  }

  async restoreProject(id: string): Promise<ServiceResponse<Campaign>> {
    return this.updateCampaign(id, { archivedAt: null });
  }

  async listArchivedProjects(limit = 100): Promise<ServiceResponse<CampaignWithCoverage[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('projects')
        .select('*')
        // An archived Brief is not an archived plan. See `PLAN_KINDS`.
        .in('kind', PLAN_KINDS as unknown as string[])
        .not('archived_at', 'is', null)
        .order('archived_at', { ascending: false })
        .limit(limit);
      if (error) throw new Error(`Archived project list failed: ${error.message}`);

      const campaigns = (data ?? []).map((row) => this.mapRow(row));
      if (campaigns.length === 0) return [];
      const { data: postCounts, error: countError } = await this.client
        .from('social_posts')
        .select('campaign_id')
        .in(
          'campaign_id',
          campaigns.map((campaign) => campaign.id),
        );
      if (countError) throw new Error(`Archived project post-count failed: ${countError.message}`);

      const countMap = new Map<string, number>();
      (postCounts ?? []).forEach((post) => {
        if (!post.campaign_id) return;
        countMap.set(post.campaign_id, (countMap.get(post.campaign_id) ?? 0) + 1);
      });
      return campaigns.map((campaign) => {
        const coverageTotal = campaign.strategy?.schedule?.length ?? 0;
        const coverageCreated = Math.min(countMap.get(campaign.id) ?? 0, coverageTotal);
        return {
          ...campaign,
          coverageCreated,
          coverageTotal,
          coveragePct: coverageTotal > 0 ? Math.round((coverageCreated / coverageTotal) * 100) : 0,
        };
      });
    });
  }

  async listConnectedPlatforms(): Promise<ServiceResponse<string[]>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();
      const platforms = await getConnectedPlatforms(
        this.client as Parameters<typeof getConnectedPlatforms>[0],
        ownerId,
      );
      return platforms;
    });
  }

  async updateWorkflowBrief(
    projectId: string,
    patch: Record<string, unknown>,
  ): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      const { data: project } = await this.client
        .from('projects')
        .select('workflow_brief')
        .eq('id', projectId)
        .single();
      const merged = {
        ...toJsonObject(project?.workflow_brief ?? {}),
        ...toJsonObject(patch),
      };
      const { error } = await this.client
        .from('projects')
        .update({ workflow_brief: merged })
        .eq('id', projectId);
      if (error) throw error;
    });
  }

  async deleteCampaign(id: string): Promise<ServiceResponse<true>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      // .select('id') makes supabase-js return the actually-deleted rows
      // — without it, RLS silently rejecting the delete returns 204 with
      // no error, and the caller has no way to know nothing happened.
      // The empty-drafts cleanup specifically needs to know if any of
      // its DELETE roundtrips were a no-op so it can surface that.
      const { data, error } = await this.client.from('projects').delete().eq('id', id).select('id');
      if (error) throw new Error(`Campaign delete failed: ${error.message}`);
      if (!data || data.length === 0) {
        throw new Error(
          'Project not deleted — no rows matched. Likely an RLS policy or ownership mismatch.',
        );
      }
      return true as const;
    });
  }

  async getWeeklyStats(
    userId: string,
  ): Promise<ServiceResponse<{ postsShippedThisWeek: number; streakWeeks: number }>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data, error } = await this.client.rpc('get_weekly_stats', { p_user_id: userId });
      if (error) throw new Error(`Weekly stats RPC failed: ${error.message}`);
      return data as { postsShippedThisWeek: number; streakWeeks: number };
    });
  }

  async updatePostMedia(postId: string, mediaUrls: string[]): Promise<ServiceResponse<unknown>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('social_posts')
        .update({ media_urls: mediaUrls })
        .eq('id', postId)
        .select()
        .single();

      if (error) throw new Error(`Post media update failed: ${error.message}`);
      return data;
    });
  }

  async generateSummary(campaignId: string): Promise<ServiceResponse<CampaignAiSummary>> {
    await this.ensureAuthenticated();

    try {
      // Call directly without this.execute to prevent BaseService from automatically logging
      // a scary "Service operation failed" error if the local function is missing (404).
      const { data, error } = await this.client.functions.invoke<CampaignAiSummary>(
        'generate-campaign-summary',
        { body: { campaignId } },
      );

      if (error) {
        // Handle local 404 gracefully if the edge function doesn't exist
        const is404 =
          error.message?.includes('404') ||
          error.message?.includes('Not Found') ||
          error.context?.status === 404 ||
          (error as any).status === 404;

        if (is404) {
          // Fallback-path hit; the edge fn is intentionally not deployed in all
          // environments. Keep this silent in prod so it stops lighting up Sentry
          // as an error (it shipped as `.warn` which the error bus forwards to
          // `captureMessage`). Dev keeps the console log for debugging.
          if (import.meta.env.DEV) {
            this.log.debug(
              'generate-campaign-summary function not found, returning fallback',
              error,
            );
          }
          return {
            data: {
              headline: '',
              insights: [],
              generatedAt: new Date().toISOString(),
              coverageAtGeneration: 100,
              version: 1,
            } as CampaignAiSummary,
            error: null,
          };
        }

        // If it's a real error, wrap it in a ServiceError
        const serviceError = this.createError(
          error.message || 'Failed to generate summary',
          'FUNCTION_ERROR',
          error,
        );
        return { data: null, error: serviceError };
      }

      return { data: data as CampaignAiSummary, error: null };
    } catch (err: any) {
      // Handle thrown exception (e.g. network error, function invocation throwing)
      const is404 =
        err.message?.includes('404') ||
        err.message?.includes('Not Found') ||
        err.status === 404 ||
        err.context?.status === 404;

      if (is404) {
        if (import.meta.env.DEV) {
          this.log.debug('generate-campaign-summary function threw 404, returning fallback', err);
        }
        return {
          data: {
            headline: '',
            insights: [],
            generatedAt: new Date().toISOString(),
            coverageAtGeneration: 100,
            version: 1,
          } as CampaignAiSummary,
          error: null,
        };
      }

      const serviceError = this.createError(
        err.message || 'Failed to generate summary',
        'FUNCTION_ERROR',
        err,
      );
      return { data: null, error: serviceError };
    }
  }

  async acceptSummary(campaignId: string): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data: campaign } = await this.client
        .from('projects')
        .select('ai_summary')
        .eq('id', campaignId)
        .single();

      if (!campaign?.ai_summary) return;

      const updated = { ...toJsonObject(campaign.ai_summary), accepted: true };
      await this.client.from('projects').update({ ai_summary: updated }).eq('id', campaignId);
    });
  }

  async dismissSummary(campaignId: string): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data: campaign } = await this.client
        .from('projects')
        .select('ai_summary')
        .eq('id', campaignId)
        .single();

      if (!campaign?.ai_summary) return;

      const updated = {
        ...toJsonObject(campaign.ai_summary),
        dismissedAt: new Date().toISOString(),
      };
      await this.client.from('projects').update({ ai_summary: updated }).eq('id', campaignId);
    });
  }

  async assignWorkflow(
    projectId: string,
    templateId: string,
    options?: { force?: boolean },
  ): Promise<ServiceResponse<Campaign>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();

      const { data: templateRow, error: tmplError } = await this.client
        .from('workflow_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (tmplError) throw new Error(`Template fetch failed: ${tmplError.message}`);

      const template = templateRow as unknown as WorkflowTemplate;
      const workflowState: WorkflowState = {
        current_step_id: template.steps[0]?.step_id ?? 1,
        status: 'not_started',
        history: [],
      };

      const { data: projectRow, error: projectFetchError } = await this.client
        .from('projects')
        .select('workflow_brief, workflow_state')
        .eq('id', projectId)
        .single();

      if (projectFetchError) throw new Error(`Project fetch failed: ${projectFetchError.message}`);

      // eng-review H7 — re-assignment guard. Block silent overwrite of an
      // active workflow. Caller can pass { force: true } after a confirm
      // dialog to deliberately replace the running template.
      const existingState = readWorkflowState(projectRow?.workflow_state);
      const isActive =
        existingState?.status === 'running' || existingState?.status === 'awaiting_approval';
      if (isActive && !options?.force) {
        const err = new Error(
          'A workflow is already running on this project. Pass force: true to replace it.',
        );
        (err as Error & { code?: string }).code = 'workflow_already_active';
        throw err;
      }

      const connected = await getConnectedPlatforms(this.client as any, ownerId);
      const newBrief = {
        ...((projectRow as { workflow_brief?: Record<string, unknown> } | null)?.workflow_brief ??
          {}),
        connected_platforms: connected,
      };

      const { data, error } = await this.client
        .from('projects')
        .update({
          active_workflow_template_id: templateId,
          workflow_state: workflowState as unknown as Json,
          workflow_brief: newBrief as unknown as Json,
        })
        .eq('id', projectId)
        .select()
        .single();

      if (error) throw new Error(`Workflow assignment failed: ${error.message}`);
      return this.mapRow(data);
    });
  }

  /**
   * Unassign the active workflow from a project, clearing both the
   * template id and the workflow_state. Used by the right-rail
   * workflow-actions menu's "Cancel workflow" option. The project
   * itself is preserved — only the workflow binding goes away.
   */
  async unassignWorkflow(projectId: string): Promise<ServiceResponse<Campaign>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('projects')
        .update({
          active_workflow_template_id: null,
          workflow_state: null,
        })
        .eq('id', projectId)
        .select()
        .single();
      if (error) throw new Error(`Workflow unassign failed: ${error.message}`);
      return this.mapRow(data);
    });
  }

  async listWorkflowTemplates(): Promise<ServiceResponse<WorkflowTemplate[]>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      // NEW1 (Mike-deep-audit) — exclude rows the user has hidden from
      // their library. RLS on hidden_workflow_templates already scopes to
      // the owner; the in-clause is what removes them from the result.
      const { data: hiddenRows } = await this.client
        .from('hidden_workflow_templates')
        .select('template_id')
        .eq('user_id', userId);
      const hiddenIds = (hiddenRows ?? []).map((r) => r.template_id);

      let query = this.client
        .from('workflow_templates')
        .select('*')
        .filter('metadata->>archived_at', 'is', null)
        .order('created_at', { ascending: false });
      if (hiddenIds.length > 0) {
        query = query.not('id', 'in', `(${hiddenIds.map((id) => `"${id}"`).join(',')})`);
      }
      const { data, error } = await query;

      if (error) throw new Error(`Template list failed: ${error.message}`);
      return (data ?? []) as unknown as WorkflowTemplate[];
    });
  }

  async listWorkflowTemplatesByInputKind(
    inputKind: 'article' | 'photos' | 'url' | 'manual',
  ): Promise<ServiceResponse<WorkflowTemplate[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('workflow_templates')
        .select('*')
        .filter('metadata->>input_kind', 'eq', inputKind)
        .filter('metadata->>archived_at', 'is', null)
        .order('created_at', { ascending: false });
      if (error) throw new Error(`Template list-by-input-kind failed: ${error.message}`);
      return (data ?? []) as unknown as WorkflowTemplate[];
    });
  }

  async hideWorkflowTemplate(templateId: string): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const { error } = await this.client
        .from('hidden_workflow_templates')
        .upsert(
          { user_id: userId, template_id: templateId },
          { onConflict: 'user_id,template_id' },
        );
      if (error) throw new Error(`Hide template failed: ${error.message}`);
    });
  }

  async unhideWorkflowTemplate(templateId: string): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const { error } = await this.client
        .from('hidden_workflow_templates')
        .delete()
        .eq('user_id', userId)
        .eq('template_id', templateId);
      if (error) throw new Error(`Unhide template failed: ${error.message}`);
    });
  }

  /**
   * P3.2 — create a custom workflow template owned by the current user.
   * RLS enforces ownership on insert/update/delete; the existing list query
   * returns built-ins (user_id IS NULL) plus the user's own custom rows.
   */
  async createWorkflowTemplate(input: {
    name: string;
    description: string;
    steps: WorkflowStep[];
    output_kinds?: WorkflowOutputKind[];
    estimate_minutes_min?: number;
    estimate_minutes_max?: number;
  }): Promise<ServiceResponse<WorkflowTemplate>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('workflow_templates')
        .insert({
          name: input.name.trim(),
          description: input.description.trim() || null,
          steps: input.steps as unknown as Json,
          output_kinds: input.output_kinds ?? null,
          estimate_minutes_min: input.estimate_minutes_min ?? null,
          estimate_minutes_max: input.estimate_minutes_max ?? null,
          user_id: userId,
          version: 1,
        })
        .select()
        .single();
      if (error) throw new Error(`Create template failed: ${error.message}`);
      return data as unknown as WorkflowTemplate;
    });
  }

  async deleteWorkflowTemplate(templateId: string): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      // RLS lets owners delete only their own templates; built-ins are
      // protected because their user_id IS NULL.
      const { error } = await this.client.from('workflow_templates').delete().eq('id', templateId);
      if (error) throw new Error(`Delete template failed: ${error.message}`);
    });
  }

  /**
   * Update a custom workflow template owned by the current user. RLS blocks
   * updates to built-ins (user_id IS NULL). Used by the edit-mode flow.
   */
  async updateWorkflowTemplate(
    templateId: string,
    patch: {
      name?: string;
      description?: string;
      steps?: WorkflowStep[];
      output_kinds?: WorkflowOutputKind[] | null;
      estimate_minutes_min?: number | null;
      estimate_minutes_max?: number | null;
    },
  ): Promise<ServiceResponse<WorkflowTemplate>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const updates: WorkflowTemplateUpdate = {};
      if (patch.name !== undefined) updates.name = patch.name.trim();
      if (patch.description !== undefined) updates.description = patch.description.trim() || null;
      if (patch.steps !== undefined) updates.steps = patch.steps as unknown as Json;
      if (patch.output_kinds !== undefined) updates.output_kinds = patch.output_kinds;
      if (patch.estimate_minutes_min !== undefined)
        updates.estimate_minutes_min = patch.estimate_minutes_min;
      if (patch.estimate_minutes_max !== undefined)
        updates.estimate_minutes_max = patch.estimate_minutes_max;

      const { data, error } = await this.client
        .from('workflow_templates')
        .update(updates)
        .eq('id', templateId)
        .select()
        .single();
      if (error) throw new Error(`Update template failed: ${error.message}`);
      return data as unknown as WorkflowTemplate;
    });
  }

  /**
   * List workflow templates the user has hidden. Used by the "Hidden" filter
   * tab on /workflows so users can find and unhide templates they previously
   * dismissed.
   */
  async listHiddenWorkflowTemplates(): Promise<ServiceResponse<WorkflowTemplate[]>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const { data: hiddenRows, error: hiddenErr } = await this.client
        .from('hidden_workflow_templates')
        .select('template_id')
        .eq('user_id', userId);
      if (hiddenErr) throw new Error(`Hidden list failed: ${hiddenErr.message}`);
      const hiddenIds = (hiddenRows ?? []).map((r) => r.template_id);
      if (hiddenIds.length === 0) return [];
      const { data, error } = await this.client
        .from('workflow_templates')
        .select('*')
        .in('id', hiddenIds)
        .order('created_at', { ascending: false });
      if (error) throw new Error(`Hidden template fetch failed: ${error.message}`);
      return (data ?? []) as unknown as WorkflowTemplate[];
    });
  }

  async getWorkflowTemplate(id: string): Promise<ServiceResponse<WorkflowTemplate | null>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('workflow_templates')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw new Error(`Template fetch failed: ${error.message}`);
      return (data ?? null) as unknown as WorkflowTemplate | null;
    });
  }

  async advanceWorkflowStep(
    projectId: string,
    nextStepId: number,
    artifacts?: Record<string, unknown>[],
  ): Promise<ServiceResponse<Campaign>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();

      const { data: project, error: fetchError } = await this.client
        .from('projects')
        .select('workflow_state, active_workflow_template_id')
        .eq('id', projectId)
        .single();
      if (fetchError) throw new Error(`Project fetch failed: ${fetchError.message}`);

      const currentState = (project.workflow_state as unknown as WorkflowState | null) ?? null;
      if (!currentState) throw new Error('No active workflow state on this project.');

      const completedEntry: WorkflowHistoryEntry = {
        step_id: currentState.current_step_id,
        status: 'completed',
        ...(artifacts && artifacts.length > 0 ? { artifacts } : {}),
      };
      const newHistory = [...currentState.history, completedEntry];

      const templateId = project.active_workflow_template_id;
      let isFinalStep = false;
      if (templateId) {
        const { data: tmpl, error: tmplError } = await this.client
          .from('workflow_templates')
          .select('steps')
          .eq('id', templateId)
          .single();
        if (tmplError) throw new Error(`Template lookup failed: ${tmplError.message}`);
        const steps = (tmpl.steps as unknown as Array<{ step_id: number }>) ?? [];
        isFinalStep = !steps.some((s) => s.step_id === nextStepId);
      }

      const nextState: WorkflowState = {
        current_step_id: isFinalStep ? currentState.current_step_id : nextStepId,
        status: isFinalStep ? 'completed' : 'running',
        history: newHistory,
      };

      const { data, error } = await this.client
        .from('projects')
        .update({ workflow_state: nextState as unknown as Json })
        .eq('id', projectId)
        .select()
        .single();
      if (error) throw new Error(`Workflow advance failed: ${error.message}`);
      return this.mapRow(data);
    });
  }

  async setWorkflowAwaitingApproval(
    projectId: string,
    pendingArtifact?: Record<string, unknown>,
  ): Promise<ServiceResponse<Campaign>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data: project, error: fetchError } = await this.client
        .from('projects')
        .select('workflow_state')
        .eq('id', projectId)
        .single();
      if (fetchError) throw new Error(`Project fetch failed: ${fetchError.message}`);
      const currentState = (project.workflow_state as unknown as WorkflowState | null) ?? null;
      if (!currentState) throw new Error('No active workflow state on this project.');

      const nextState: WorkflowState = {
        ...currentState,
        status: 'awaiting_approval',
        ...(pendingArtifact ? { pending_artifact: pendingArtifact } : {}),
      };

      const { data, error } = await this.client
        .from('projects')
        .update({ workflow_state: nextState as unknown as Json })
        .eq('id', projectId)
        .select()
        .single();
      if (error) throw new Error(`Workflow approval flag failed: ${error.message}`);
      return this.mapRow(data);
    });
  }

  private mapRow(row: Record<string, unknown>): Campaign {
    return {
      id: row.id as string,
      userId: row.user_id as string,
      name: row.name as string,
      goal: (row.goal as string) || null,
      timelineStart: (row.timeline_start as string) || null,
      timelineEnd: (row.timeline_end as string) || null,
      chatSessionId: (row.chat_session_id as string) || null,
      status: row.status as Campaign['status'],
      strategy: (row.strategy as StoredProjectStrategy) || null,
      platforms: (row.platforms as string[]) || null,
      planType: (row.plan_type as string) || null,
      collectionId: (row.collection_id as string) || null,
      projectId: null,
      kind: ((row.kind as ProjectKind) || 'campaign') as ProjectKind,
      notes: (row.notes as string) || null,
      icon: (row.icon as string) || null,
      color: (row.color as string) || null,
      aiSummary: (row.ai_summary as CampaignAiSummary) || null,
      brandVoiceId: (row.brand_voice_id as string) ?? null,
      brandKitId: (row.brand_kit_id as string) ?? null,
      version: typeof row.version === 'number' ? (row.version as number) : 0,
      createdAt: row.created_at as string,
      updatedAt: row.updated_at as string,
      activeWorkflowTemplateId: (row.active_workflow_template_id as string) || null,
      workflowState: (row.workflow_state as WorkflowState) || null,
      workflowBrief: (row.workflow_brief as Record<string, unknown>) ?? null,
      archivedAt: (row.archived_at as string) ?? null,
    };
  }
}

export const campaignService = new CampaignServiceClass();
